import { useState, useRef, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Bot, X, Send, Loader2, Sparkles, GripVertical, Maximize2, Minimize2, Copy, Mail, Check, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';

interface Proposal {
  kind: string;
  title: string;
  details: Array<{ label: string; value: string; warn?: boolean }>;
  args: Record<string, any>;
  rowCount: number;
  blockedReason?: string;
  assumed?: string[];
  needsRecipient?: boolean;
  recipientChoices?: Array<{ label: string; email: string }>;
}
interface NavTarget { path: string; label: string; note?: string }
interface Msg {
  role: 'user' | 'assistant';
  content: string;
  sql?: string;
  rowCount?: number;
  rows?: any[];
  proposal?: Proposal;
  navigation?: NavTarget;
  /** Set once the user has decided, so a card can never be actioned twice. */
  settled?: 'sent' | 'cancelled';
}
interface Agent { key: string; name: string; description: string; samples: string[] }

/**
 * Confirmation card for an action the assistant proposed.
 *
 * Nothing is sent until Send is pressed here. The card exists because the model fills gaps: asked to
 * mail a report it will happily supply a service type nobody mentioned. Everything it decided is on
 * the card, and anything it INVENTED is called out in the amber note — visible before it costs
 * anything, rather than discovered by the merchant who received the wrong file.
 */
function ProposalCard({ p, settled, onSend, onCancel, busy }: {
  p: Proposal; settled?: 'sent' | 'cancelled'; onSend: (to: string) => void; onCancel: () => void; busy: boolean;
}) {
  // Starts EMPTY when the user named nobody — an address must be chosen or typed, not defaulted.
  const [to, setTo] = useState(p.needsRecipient ? '' : String(p.args.to ?? ''));
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to.trim());
  return (
    <div className="mt-1 border border-amber-300 bg-amber-50 rounded-xl p-3 text-slate-800">
      <div className="flex items-center gap-1.5 font-semibold text-[13px] mb-2">
        <Mail size={14} className="text-amber-700" /> {p.title}
      </div>
      <table className="text-[11px] w-full mb-2">
        <tbody>
          {p.details.map((d) => (
            <tr key={d.label}>
              <td className="pr-3 py-0.5 text-slate-500 align-top whitespace-nowrap">{d.label}</td>
              <td className={`py-0.5 font-medium break-all ${d.warn ? 'text-rose-700' : ''}`}>{d.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {p.needsRecipient && settled !== 'sent' && settled !== 'cancelled' && (
        <div className="mb-2">
          <div className="text-[11px] font-medium text-slate-700 mb-1">Who should this go to?</div>
          <input
            value={to}
            onChange={(e) => setTo(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && valid && !busy) onSend(to.trim()); }}
            placeholder="name@company.com"
            className={`w-full px-2 py-1 text-[11px] border rounded-lg outline-none bg-white ${to && !valid ? 'border-rose-300' : 'border-slate-300'}`}
          />
          {!!p.recipientChoices?.length && (
            <div className="flex flex-wrap gap-1 mt-1">
              {p.recipientChoices.map((c) => (
                <button key={c.email} onClick={() => setTo(c.email)}
                  className="text-[10px] px-1.5 py-0.5 rounded-full border border-slate-300 bg-white hover:bg-slate-50">
                  {c.label}: {c.email}
                </button>
              ))}
            </div>
          )}
          {to && !valid && <div className="text-[10px] text-rose-700 mt-1">That is not a valid email address.</div>}
        </div>
      )}
      {!!p.assumed?.length && (
        <div className="text-[10px] text-amber-900 bg-amber-100 border border-amber-200 rounded p-1.5 mb-2">
          <span className="font-semibold">I filled in some gaps —</span>
          <ul className="list-disc ml-4 mt-0.5">{p.assumed.map((a) => <li key={a}>{a}</li>)}</ul>
        </div>
      )}
      {p.blockedReason && <div className="text-[11px] text-rose-700 mb-2">{p.blockedReason}</div>}
      {settled === 'sent' ? (
        <div className="text-[11px] text-emerald-700 font-medium inline-flex items-center gap-1"><Check size={12} /> Sent</div>
      ) : settled === 'cancelled' ? (
        <div className="text-[11px] text-slate-500">Cancelled — nothing was sent.</div>
      ) : (
        <div className="flex items-center gap-2">
          <button onClick={() => onSend(to.trim())} disabled={busy || !!p.blockedReason || !valid}
            title={!valid ? 'Enter the recipient first' : undefined}
            className="px-3 py-1 text-[11px] bg-emerald-600 text-white rounded-lg inline-flex items-center gap-1 disabled:opacity-50">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
            {valid ? `Send to ${to.trim()}` : 'Send it'}
          </button>
          <button onClick={onCancel} disabled={busy} className="px-3 py-1 text-[11px] border border-slate-300 rounded-lg hover:bg-white disabled:opacity-50">
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

// The floating widget can be dragged anywhere (so it never blocks other buttons/layout). Its
// position is the launcher's top-left, persisted per-browser; the chat panel is anchored to it and
// kept fully on-screen.
const LAUNCHER = 56;      // launcher button size (px)
// Starting size. The panel is RESIZABLE now: a 380px column could show a report only a few
// characters wide, so any question answered with rows was unreadable in the place it was answered.
const PANEL_W = 460;
const PANEL_H = 620;
const MIN_W = 340, MIN_H = 320;
const POS_KEY = 'gaint.chatbot.pos';
const SIZE_KEY = 'gaint.chatbot.size';
const AGENT_KEY = 'gaint.chatbot.agent';
const EDGE = 8;           // min gap from the viewport edges
const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

type Pos = { x: number; y: number };
type Size = { w: number; h: number };

function defaultPos(): Pos {
  const w = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const h = typeof window !== 'undefined' ? window.innerHeight : 800;
  return { x: w - LAUNCHER - 24, y: h - LAUNCHER - 96 };   // bottom-right (matches the old default)
}
function loadPos(): Pos {
  try {
    const raw = localStorage.getItem(POS_KEY);
    if (raw) { const p = JSON.parse(raw); if (typeof p?.x === 'number' && typeof p?.y === 'number') return { x: p.x, y: p.y }; }
  } catch { /* ignore */ }
  return defaultPos();
}
function loadSize(): Size {
  try {
    const raw = localStorage.getItem(SIZE_KEY);
    if (raw) { const s = JSON.parse(raw); if (typeof s?.w === 'number' && typeof s?.h === 'number') return { w: s.w, h: s.h }; }
  } catch { /* ignore */ }
  return { w: PANEL_W, h: PANEL_H };
}

/**
 * Render a row set as a real table.
 *
 * A 40-row answer used to arrive as one paragraph of prose written by the model, which is the wrong
 * shape for a report — you cannot scan it, sort it or copy a column out of it. The model's sentence
 * is still shown above as the summary; this is the data behind it.
 */
function ResultTable({ rows, rowCount }: { rows: any[]; rowCount?: number }) {
  const cols = Object.keys(rows[0] ?? {});
  if (!cols.length) return null;
  const shown = rows.length;
  const copy = () => {
    const tsv = [cols.join('\t'), ...rows.map((r) => cols.map((c) => fmt(r[c])).join('\t'))].join('\n');
    navigator.clipboard?.writeText(tsv).catch(() => { /* clipboard blocked — the table is still on screen */ });
  };
  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] text-slate-500">
          {rowCount != null && rowCount > shown ? `${shown} of ${rowCount} rows` : `${shown} row${shown === 1 ? '' : 's'}`}
        </span>
        <button onClick={copy} className="text-[10px] text-emerald-700 hover:underline inline-flex items-center gap-1">
          <Copy size={10} /> Copy for Excel
        </button>
      </div>
      {/* Scrolls inside its own box — the chat column must never scroll sideways. */}
      <div className="max-h-64 overflow-auto border border-slate-200 rounded-lg">
        <table className="text-[11px] border-collapse w-full">
          <thead className="sticky top-0 bg-slate-100">
            <tr>{cols.map((c) => <th key={c} className="text-left font-semibold px-2 py-1 whitespace-nowrap border-b border-slate-200">{c}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className={i % 2 ? 'bg-slate-50' : 'bg-white'}>
                {cols.map((c) => (
                  <td key={c} className={`px-2 py-1 whitespace-nowrap border-b border-slate-100 ${typeof r[c] === 'number' || /^-?[\d,.]+$/.test(String(r[c] ?? '')) ? 'text-right tabular-nums' : ''}`}>
                    {fmt(r[c])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Nulls read as an empty cell, not "null"; timestamps lose the T/Z noise. */
function fmt(v: any): string {
  if (v === null || v === undefined) return '';
  const s = String(v);
  const iso = s.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  return iso ? `${iso[1]} ${iso[2]}` : s;
}

export function ChatbotWidget() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [pos, setPos] = useState<Pos>(loadPos);
  const [size, setSize] = useState<Size>(loadSize);

  // Which agent is answering. The list comes from the server and reflects THIS user's permissions,
  // so an agent someone isn't entitled to never appears; the picker hides itself when only one is
  // available rather than showing a dropdown with nothing to choose.
  const { data: agents = [] } = useQuery({
    queryKey: ['ai-agents'],
    queryFn: async () => (await api.get<Agent[]>('/ai-chatbot/agents')).data,
    staleTime: 5 * 60_000,
  });
  const [agentKey, setAgentKey] = useState<string>(() => {
    try { return localStorage.getItem(AGENT_KEY) || 'general'; } catch { return 'general'; }
  });
  // A remembered agent that this user is no longer entitled to must not stick — fall back to the
  // first one they DO have, so the picker can never sit on an option every question would refuse.
  useEffect(() => {
    if (agents.length && !agents.some((a) => a.key === agentKey)) setAgentKey(agents[0].key);
  }, [agents, agentKey]);
  const agent = agents.find((a) => a.key === agentKey);
  const pickAgent = (k: string) => {
    setAgentKey(k);
    try { localStorage.setItem(AGENT_KEY, k); } catch { /* ignore */ }
  };
  const [msgs, setMsgs] = useState<Msg[]>([
    { role: 'assistant', content: user?.kind === 'merchant'
      ? 'Namaste! Pucho — kitne orders aaj hue, kitne delivered hue? Ask in English, Hindi or Hinglish.'
      : 'Hi! Ask me anything about merchants, orders, NDR, remittance — in English, Hindi, or Hinglish.' },
  ]);
  const endRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{ sx: number; sy: number; ox: number; oy: number } | null>(null);
  const movedRef = useRef(false);

  const ask = useMutation({
    // One question is TWO round-trips to the model (write the SQL, then phrase the answer from the
    // rows), so the client's 30s default was never enough — a measured question took 68s and the
    // browser gave up at 30 while the server was still working, reported as "timeout of 30000ms
    // exceeded". Raised here only: a global bump would leave every other broken request hanging for
    // minutes instead of failing fast. The server aborts each model call at 60s, so the worst honest
    // case sits inside this.
    mutationFn: async (prompt: string) => (await api.post<{ ok: boolean; answer: string; sql?: string; rowCount?: number; rows?: any[]; proposal?: Proposal; navigation?: NavTarget }>(
      '/ai-chatbot/ask', { prompt, agentKey }, { timeout: 180_000 },
    )).data,
    onSuccess: (r) => setMsgs((m) => [...m, {
      role: 'assistant',
      content: r.proposal ? 'Ready to send — check this first:' : r.answer,
      sql: r.sql, rowCount: r.rowCount, rows: r.rows, proposal: r.proposal, navigation: r.navigation,
    }]),
    onError: (e: any) => {
      // Say what actually happened. "timeout of 180000ms exceeded" tells a user nothing they can act on.
      const msg = e?.code === 'ECONNABORTED'
        ? 'The AI took too long to answer and the request was stopped. Try a simpler question, or try again in a minute — the model provider is slow right now.'
        : e?.response?.data?.message ?? e?.message ?? 'Sorry, something went wrong.';
      setMsgs((m) => [...m, { role: 'assistant', content: msg }]);
    },
  });

  // Confirming a proposal. `settled` is stamped on the message so a card cannot be pressed twice —
  // a second click here would mail the merchant the same file again.
  const [sendingIdx, setSendingIdx] = useState<number | null>(null);
  const confirm = useMutation({
    // The recipient comes from the CARD, not the proposal — it may have been typed or picked just now.
    mutationFn: async ({ p, to }: { p: Proposal; idx: number; to: string }) =>
      (await api.post('/ai-chatbot/execute', { tool: p.kind, args: { ...p.args, to } }, { timeout: 180_000 })).data,
    onSuccess: (r: any, v) => {
      setMsgs((m) => m.map((msg, i) => (i === v.idx ? { ...msg, settled: 'sent' as const } : msg)));
      setMsgs((m) => [...m, { role: 'assistant', content: `Sent to ${v.to} — ${r?.rows ?? 0} rows as ${r?.fileName ?? 'a CSV'}.` }]);
      setSendingIdx(null);
    },
    onError: (e: any, v) => {
      // The card stays actionable — a failed send is usually a fixable address or an expired session,
      // and marking it settled would force the user to compose the whole request again.
      setMsgs((m) => [...m, { role: 'assistant', content: e?.response?.data?.message ?? 'The send failed — nothing was emailed.' }]);
      setSendingIdx(null);
      void v;
    },
  });

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs, open]);

  // Escape always closes the panel — a second way out that does not depend on the header rendering
  // correctly. The close button was once pushed off the edge by a long title and clipped away, which
  // left no way to shut the panel at all; a keyboard escape cannot be laid out wrong.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Keep the widget inside the viewport on first mount and whenever the window resizes.
  useEffect(() => {
    const fit = () => setPos((p) => ({
      x: clamp(p.x, EDGE, window.innerWidth - LAUNCHER - EDGE),
      y: clamp(p.y, EDGE, window.innerHeight - LAUNCHER - EDGE),
    }));
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  const savePos = (p: { x: number; y: number }) => { try { localStorage.setItem(POS_KEY, JSON.stringify(p)); } catch { /* ignore */ } };
  const saveSize = (s: Size) => { try { localStorage.setItem(SIZE_KEY, JSON.stringify(s)); } catch { /* ignore */ } };

  // Resize from the panel's bottom-right grip. The panel is anchored to the launcher's TOP-LEFT
  // corner while growing down-right, so dragging the grip moves only the far edge — the header stays
  // put instead of sliding out from under the pointer.
  const resize = useRef<{ sx: number; sy: number; ow: number; oh: number } | null>(null);
  const onResizeDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    resize.current = { sx: e.clientX, sy: e.clientY, ow: size.w, oh: size.h };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  const onResizeMove = (e: React.PointerEvent) => {
    const r = resize.current; if (!r) return;
    e.stopPropagation();
    setSize({
      w: clamp(r.ow + (e.clientX - r.sx), MIN_W, window.innerWidth - 2 * EDGE),
      h: clamp(r.oh + (e.clientY - r.sy), MIN_H, window.innerHeight - 2 * EDGE),
    });
  };
  const onResizeUp = (e: React.PointerEvent) => {
    if (!resize.current) return;
    resize.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    setSize((s) => { saveSize(s); return s; });
  };
  /** Snap to a tall, wide shape for reading a report, or back to the compact chat size. */
  const toggleWide = () => {
    const wide = size.w >= 700;
    const next: Size = wide
      ? { w: PANEL_W, h: PANEL_H }
      : { w: clamp(900, MIN_W, window.innerWidth - 2 * EDGE), h: clamp(700, MIN_H, window.innerHeight - 2 * EDGE) };
    setSize(next); saveSize(next);
  };

  // Shared drag handlers — used by the launcher (when closed) and the panel header (when open).
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;                                   // left / primary only
    if ((e.target as HTMLElement).closest('[data-no-drag]')) return; // e.g. the close button
    drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };
    movedRef.current = false;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current; if (!d) return;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    if (!movedRef.current && Math.hypot(dx, dy) < 4) return;      // tiny movement = a click, not a drag
    movedRef.current = true;
    setPos({
      x: clamp(d.ox + dx, EDGE, window.innerWidth - LAUNCHER - EDGE),
      y: clamp(d.oy + dy, EDGE, window.innerHeight - LAUNCHER - EDGE),
    });
  };
  const onPointerUp = (e: React.PointerEvent) => {
    const wasDragging = !!drag.current;
    drag.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    if (wasDragging && movedRef.current) setPos((p) => { savePos(p); return p; });
  };

  const submit = () => {
    const q = input.trim();
    if (!q || ask.isPending) return;
    setMsgs((m) => [...m, { role: 'user', content: q }]);
    setInput('');
    ask.mutate(q);
  };

  // Panel anchored to the launcher, then clamped so the whole panel stays on-screen.
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const panelLeft = clamp(pos.x + LAUNCHER - size.w, EDGE, Math.max(EDGE, vw - size.w - EDGE));
  const panelTop = clamp(pos.y + LAUNCHER - size.h, EDGE, Math.max(EDGE, vh - size.h - EDGE));

  return (
    <>
      {!open && (
        <button
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onClick={() => { if (movedRef.current) { movedRef.current = false; return; } setOpen(true); }}
          style={{ left: pos.x, top: pos.y }}
          className="fixed z-[9998] bg-gradient-to-br from-emerald-500 to-emerald-700 text-white w-14 h-14 rounded-full shadow-2xl flex items-center justify-center hover:scale-105 transition touch-none select-none cursor-grab active:cursor-grabbing"
          title="Click to ask · Drag to move">
          <Bot size={26} />
        </button>
      )}
      {open && (
        <div style={{ left: panelLeft, top: panelTop, width: size.w, height: size.h }}
          className="fixed z-[9999] max-w-[calc(100vw-1rem)] max-h-[calc(100vh-1rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
          <div
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            className="px-4 py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white flex items-center justify-between cursor-move touch-none select-none"
            title="Drag to move">
            {/* min-w-0 + flex-1 is what lets this block SHRINK. `truncate` on the text inside only
                works if every flex ancestor is allowed to go below its content width — without it the
                long agent description ("Orders, NDR, pickups… if you have HR access") held the block
                at full width and pushed the expand/close buttons past the panel edge, where
                overflow-hidden clipped them away entirely. */}
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <GripVertical size={16} className="opacity-70 shrink-0" />
              <Sparkles size={18} className="shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{agent?.name ?? 'Gaint AI Assistant'}</div>
                <div className="text-[10px] opacity-80 truncate">{agent?.description ?? 'Ask in English / Hindi / Hinglish'}</div>
              </div>
            </div>
            {/* shrink-0 so the controls keep their width no matter how long the title gets. */}
            <div className="flex items-center gap-1 shrink-0 ml-2">
              <button data-no-drag onClick={toggleWide} title={size.w >= 700 ? 'Back to compact size' : 'Expand to read reports'}
                className="text-white/80 hover:text-white p-1">
                {size.w >= 700 ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </button>
              <button data-no-drag onClick={() => setOpen(false)} className="text-white/80 hover:text-white p-1"><X size={18} /></button>
            </div>
          </div>
          {agents.length > 1 && (
            <div data-no-drag className="flex items-center gap-1 px-2 py-1.5 border-b border-slate-200 bg-white overflow-x-auto">
              {agents.map((a) => (
                <button key={a.key} onClick={() => pickAgent(a.key)} title={a.description}
                  className={`text-[11px] px-2 py-1 rounded-full border whitespace-nowrap shrink-0 ${a.key === agentKey ? 'bg-emerald-50 border-emerald-300 text-emerald-800 font-medium' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                  {a.name}
                </button>
              ))}
            </div>
          )}
          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50">
            {/* Starter questions — the fastest way to learn what an agent actually covers, and they
                change with the agent so the examples always match the scope in front of you. */}
            {!!agent?.samples?.length && msgs.length <= 1 && (
              <div className="flex flex-wrap gap-1.5 pb-1">
                {agent.samples.map((s) => (
                  <button key={s} onClick={() => { setMsgs((m) => [...m, { role: 'user', content: s }]); ask.mutate(s); }}
                    disabled={ask.isPending}
                    className="text-[11px] px-2 py-1 rounded-full border border-emerald-200 text-emerald-700 bg-white hover:bg-emerald-50 disabled:opacity-50">
                    {s}
                  </button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${m.role === 'user' ? 'max-w-[85%] bg-emerald-600 text-white rounded-br-sm' : `${m.rows?.length ? 'max-w-full w-full' : 'max-w-[85%]'} bg-white border border-slate-200 text-slate-800 rounded-bl-sm`}`}>
                  {m.content}
                  {m.navigation && (
                    // In-app navigation, not a page load — the chat stays open and keeps its history,
                    // so you can carry on giving instructions from wherever you land.
                    <button
                      onClick={() => { navigate(m.navigation!.path); }}
                      className="mt-1.5 w-full text-left px-3 py-2 rounded-lg border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 transition inline-flex items-center justify-between gap-2">
                      <span className="min-w-0">
                        <span className="block text-[12px] font-semibold text-emerald-800 truncate">{m.navigation.label}</span>
                        <span className="block text-[10px] text-emerald-700/70 truncate">{m.navigation.path}</span>
                      </span>
                      <ArrowRight size={14} className="text-emerald-700 shrink-0" />
                    </button>
                  )}
                  {m.proposal && (
                    <ProposalCard
                      p={m.proposal}
                      settled={m.settled}
                      busy={sendingIdx === i}
                      onSend={(to) => { setSendingIdx(i); confirm.mutate({ p: m.proposal!, idx: i, to }); }}
                      onCancel={() => setMsgs((prev) => prev.map((x, j) => (j === i ? { ...x, settled: 'cancelled' as const } : x)))}
                    />
                  )}
                  {!!m.rows?.length && <ResultTable rows={m.rows} rowCount={m.rowCount} />}
                  {m.sql && (
                    <details className="mt-1.5 text-[10px] text-slate-500">
                      <summary className="cursor-pointer hover:underline">SQL · {m.rowCount ?? 0} rows</summary>
                      <pre className="mt-1 bg-slate-100 p-1.5 rounded overflow-auto">{m.sql}</pre>
                    </details>
                  )}
                </div>
              </div>
            ))}
            {ask.isPending && <div className="flex justify-start"><div className="bg-white border border-slate-200 rounded-2xl px-3 py-2 text-sm text-slate-500 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Soch raha hoon…</div></div>}
            <div ref={endRef} />
          </div>
          <div className="border-t border-slate-200 p-2 flex items-center gap-1 bg-white">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
              placeholder="Aaj kitne orders hue?"
              className="flex-1 px-3 py-2 text-sm outline-none" />
            <button onClick={submit} disabled={!input.trim() || ask.isPending} className="p-2 bg-emerald-600 text-white rounded-lg disabled:opacity-50">
              {ask.isPending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </div>
          {/* Resize grip. touch-none stops the browser treating the drag as a scroll gesture. */}
          <div
            onPointerDown={onResizeDown}
            onPointerMove={onResizeMove}
            onPointerUp={onResizeUp}
            title="Drag to resize"
            className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize touch-none select-none"
            style={{ background: 'linear-gradient(135deg, transparent 50%, rgb(148 163 184) 50%, rgb(148 163 184) 60%, transparent 60%, transparent 70%, rgb(148 163 184) 70%, rgb(148 163 184) 80%, transparent 80%)' }}
          />
        </div>
      )}
    </>
  );
}
